import React, { useEffect, useState, useCallback, useMemo } from 'react';

import debounce from 'lodash/debounce';

import AutocompleteDropDown from 'components/uiLibrary/FormInputs/AutocompleteDropDown';
import MultiSelectDropdown from 'components/uiLibrary/Inputs/MultiSelectDropdown';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { GOOGLE_OLD_TRACKING_SERVICES } from 'components/Globals/Analytics/constants';
import CommonTrans from 'components/Globals/CommonTrans';

import { RESTRICTED_COMPOSER_NAMES, RESTRICTED_WORK_NAMES } from 'constants/consts';
import { AGGREGATION_TYPES, SORT_OPTION_VALUES } from 'constants/index';

import { useQuery } from 'utils/react-query';
import { trackSearchFilter, trackSearch } from 'utils/tracking';
import { getWorkTitle } from 'utils/works';

import { transformProductionAggregationYears } from 'containers/Globals/transformers';
import searchQueries from 'containers/Search/queries';

import { useTranslation } from 'src/i18n';

import { TP } from 'operabase-router/constants';

import classes from './AggregationField.module.scss';

const SingleSelect = ({
  className,
  label,
  value,
  options = [],
  onChange,
  onQueryChange,
  renderMenuListItem,
  disabled = false,
  isLoading = false,
  disableClearable = false,
  popperStyles,
  trackingData = {},
  mobileDrawerProps,
  textFieldProps = {},
  variant,
}) => {
  const track = useTracking();
  const [localQuery, setLocalQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const onInputChange = useCallback(
    (event, fieldValue, reason) => {
      if (reason === 'clear') {
        onChange('');
      }

      if (event?.type === 'change') {
        setLocalQuery(fieldValue);
      }
    },
    [onChange],
  );

  const endAdornment = useMemo(() => {
    if (value?.id) {
      return (
        <SpriteIcon
          icon="close"
          size={20}
          onClick={e => {
            e.stopPropagation();
            onInputChange(e, '', 'clear');
          }}
        />
      );
    }

    return <SpriteIcon icon={isExpanded ? 'keyboard_arrow_up' : 'expand_more'} size={20} className={classes.icon} />;
  }, [value, isExpanded, onInputChange]);

  const debounceOnQueryChange = useMemo(() => debounce(onQueryChange, 300), [onQueryChange]);

  useEffect(() => {
    if (isExpanded || mobileDrawerProps?.allowMobileDrawer) {
      debounceOnQueryChange(localQuery);
    }
  }, [localQuery, isExpanded, debounceOnQueryChange, mobileDrawerProps?.allowMobileDrawer]);

  return (
    <AutocompleteDropDown
      key={label}
      className={className}
      label={label}
      options={options}
      value={value}
      renderMenuListItem={renderMenuListItem}
      onChange={(e, fieldValue) => {
        if (onChange) {
          onChange(fieldValue || '');
        }
      }}
      onInputChange={onInputChange}
      isLoading={isExpanded && isLoading}
      onOpen={() => {
        setIsExpanded(true);
      }}
      onClose={() => {
        setIsExpanded(false);
      }}
      onClick={() => {
        track.click({
          ...trackingData,
        });
      }}
      disabled={disabled}
      disableClearable={disableClearable}
      trackingData={{ searchQuery: localQuery, ...trackingData }}
      InputProps={{
        endAdornment,
      }}
      popperStyles={popperStyles}
      mobileDrawerProps={mobileDrawerProps}
      textFieldProps={{
        ...textFieldProps,
        ...(textFieldProps?.typingPlaceholder && isExpanded && { placeholder: textFieldProps?.typingPlaceholder }),
      }}
      variant={variant}
    />
  );
};

const Label = ({ label, variant }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (!variant) {
    return label || null;
  }

  return (
    <Typography variant={variant} size="12" truncate>
      <CommonTrans ns="NS_APP_GLOBALS" i18nKey={t(`${TP}.FN_FILTER_BY_OPTION`, { option: label })} />
    </Typography>
  );
};

const AggregationField = ({
  label,
  labelVariant,
  variant,
  endpoint,
  aggregationType,
  selectedOptions,
  onChange,
  queryParams = {},
  queryConfig = {},
  disabled = false,
  multiple = false,
  disableClearable = false,
  styles,
  prefixContent,
  renderMenuListItem,
  popperStyles,
  trackingData = {},
  mobileDrawerProps,
  errorMessage,
  textFieldProps = {},
}) => {
  const track = useTracking();
  const [query, setQuery] = useState('');
  const { data, isLoading } = useQuery(
    searchQueries.getDropDownList({
      endpoint,
      queryParams: {
        ...queryParams,
        aggregation_query: query,
        aggregation_type: aggregationType,
        ...(aggregationType === AGGREGATION_TYPES.YEAR ? { sort: SORT_OPTION_VALUES.YEAR_DESC } : {}),
      },
      queryConfig: {
        keepPreviousData: true,
        select: response => {
          if (aggregationType === AGGREGATION_TYPES.YEAR) {
            return transformProductionAggregationYears(response?.data?.data);
          }

          const items = (response?.data?.data || [])?.map(item => {
            let itemLabel = item?.name;

            if (aggregationType === AGGREGATION_TYPES.WORK) {
              itemLabel = getWorkTitle(item, true, true);
            }

            return { ...item, label: itemLabel };
          });

          if (aggregationType === AGGREGATION_TYPES.WORK) {
            return items.filter(
              item =>
                !RESTRICTED_WORK_NAMES.includes(item?.name) &&
                !item.creators?.some(({ profile }) => RESTRICTED_COMPOSER_NAMES.includes(profile?.name)),
            );
          }

          if (aggregationType === AGGREGATION_TYPES.CREATOR) {
            return items.filter(item => !RESTRICTED_COMPOSER_NAMES.includes(item?.name));
          }

          return items;
        },
        enabled: !!aggregationType && !!endpoint && !disabled,
        ...queryConfig,
        onSuccess: response => {
          if (query?.length > 0) {
            track.click(trackSearch(aggregationType, query), GOOGLE_OLD_TRACKING_SERVICES);
          }

          if (typeof queryConfig?.onSuccess === 'function') {
            queryConfig.onSuccess(response);
          }
        },
      },
    }),
  );

  const onChangeHandler = useCallback(
    props => {
      if (onChange) {
        const defaultValue = multiple ? [] : '';
        const value = props || defaultValue;

        track.click(
          trackSearchFilter(aggregationType, value?.label || value?.name, value?.slug),
          GOOGLE_OLD_TRACKING_SERVICES,
        );
        onChange(value);
      }
    },
    [onChange, multiple, track, aggregationType],
  );

  if (multiple) {
    return (
      <MultiSelectDropdown
        label={label}
        options={data}
        disabled={disabled}
        selectedOptions={selectedOptions}
        onChange={onChangeHandler}
        styles={styles}
        prefixContent={prefixContent}
        trackingData={{ searchQuery: query, ...trackingData }}
        mobileDrawerProps={mobileDrawerProps}
        errorMessage={errorMessage}
        variant={variant}
      />
    );
  }

  return (
    <SingleSelect
      label={variant ? <Label label={label} variant={labelVariant} /> : label}
      value={selectedOptions?.[0]}
      options={data}
      onChange={onChangeHandler}
      onQueryChange={setQuery}
      isLoading={isLoading}
      disabled={disabled}
      className={styles?.root}
      renderMenuListItem={renderMenuListItem}
      disableClearable={disableClearable}
      trackingData={trackingData}
      popperStyles={popperStyles}
      mobileDrawerProps={mobileDrawerProps}
      textFieldProps={textFieldProps}
      variant={variant}
    />
  );
};

export default AggregationField;
